// ViewContent / AddToCart / Search Meta events — ported from the original
// src/lib/metaTracking.ts, adapted for a Shopify theme: forwards through the
// companion app's App Proxy instead of a Next.js API route. Runs on ordinary
// storefront pages with full cookie/localStorage access (unlike the
// sandboxed Web Pixel, which only handles the checkout-only events — see
// extensions/web-pixel-marika-meta in the companion app).
(function () {
  var FBP_COOKIE = "_fbp";
  var FBC_COOKIE = "_fbc";
  var FBC_FALLBACK_STORAGE_KEY = "marika-fbc-fallback";
  var EXTERNAL_ID_STORAGE_KEY = "marika-external-id";
  var FBC_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
  var DEDUPE_WINDOW_MS = 1500;
  var recentEventKeys = {};
  // Set once in init() from the logged-in customer, if any — merged into
  // every event's user_data so ViewContent/AddToCart/Search carry the same
  // match-quality signals (email/phone/name/address) that Purchase already
  // gets from checkout, instead of only ever sending fbp/fbc/external_id.
  var currentCustomer = null;

  function getCookie(name) {
    var match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : undefined;
  }

  function setCookie(name, value, maxAgeSeconds) {
    document.cookie = name + "=" + encodeURIComponent(value) + "; path=/; max-age=" + maxAgeSeconds + "; SameSite=Lax";
  }

  function readLocalStorage(key) {
    try {
      return window.localStorage.getItem(key) || undefined;
    } catch (error) {
      return undefined;
    }
  }

  function writeLocalStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      // Storage unavailable (private mode, quota) — cookie remains the source of truth.
    }
  }

  // Meta's own pixel script sets `_fbc` once fbevents.js's network request
  // finishes — deriving it from `fbclid` ourselves closes the race for
  // events fired right after landing on an ad click.
  function ensureFbc() {
    var existingCookie = getCookie(FBC_COOKIE);
    if (existingCookie) {
      writeLocalStorage(FBC_FALLBACK_STORAGE_KEY, existingCookie);
      return existingCookie;
    }

    var fbclid = new URLSearchParams(window.location.search).get("fbclid");
    if (fbclid) {
      var fbc = "fb.1." + Date.now() + "." + fbclid;
      setCookie(FBC_COOKIE, fbc, FBC_COOKIE_MAX_AGE_SECONDS);
      writeLocalStorage(FBC_FALLBACK_STORAGE_KEY, fbc);
      return fbc;
    }

    var storedFbc = readLocalStorage(FBC_FALLBACK_STORAGE_KEY);
    if (storedFbc) {
      setCookie(FBC_COOKIE, storedFbc, FBC_COOKIE_MAX_AGE_SECONDS);
      return storedFbc;
    }

    return undefined;
  }

  function getPersistentExternalId() {
    var existing = readLocalStorage(EXTERNAL_ID_STORAGE_KEY);
    if (existing) return existing;
    var generated =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : "mrk-" + Date.now() + "-" + Math.random().toString(36).slice(2);
    writeLocalStorage(EXTERNAL_ID_STORAGE_KEY, generated);
    return generated;
  }

  function trackMetaEvent(eventName, customData) {
    var now = Date.now();
    var eventId = "marika-" + eventName + "-" + now + "-" + Math.random().toString(36).slice(2);
    var dedupeKey = eventName + ":" + JSON.stringify(customData || {});

    if (recentEventKeys[dedupeKey] && now - recentEventKeys[dedupeKey] < DEDUPE_WINDOW_MS) return;
    recentEventKeys[dedupeKey] = now;

    var script = document.currentScript || document.querySelector("[data-proxy-url]");
    var proxyUrl = (script && script.dataset.proxyUrl) || "/apps/marika/meta-events";

    var userData = {
      external_id: (currentCustomer && currentCustomer.email) || getPersistentExternalId(),
      fbp: getCookie(FBP_COOKIE),
      fbc: ensureFbc(),
    };

    if (currentCustomer) {
      if (currentCustomer.email) userData.email = currentCustomer.email;
      if (currentCustomer.phone) userData.phone = currentCustomer.phone;
      if (currentCustomer.firstName) userData.firstName = currentCustomer.firstName;
      if (currentCustomer.lastName) userData.lastName = currentCustomer.lastName;
      if (currentCustomer.city) userData.city = currentCustomer.city;
      if (currentCustomer.state) userData.state = currentCustomer.state;
      if (currentCustomer.country) userData.country = currentCustomer.country;
    }

    fetch(proxyUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        event_name: eventName,
        event_id: eventId,
        event_source_url: window.location.href,
        custom_data: customData || {},
        user_data: userData,
      }),
    }).catch(function () {
      // Best-effort — tracking failures should never affect the storefront.
    });
  }

  function init() {
    var dataScript = document.getElementById("marika-meta-page-data");
    if (!dataScript) return;

    var pageData;
    try {
      pageData = JSON.parse(dataScript.textContent);
    } catch (error) {
      return;
    }

    currentCustomer = pageData.customer || null;

    if (pageData.template === "product" && pageData.product) {
      trackMetaEvent("ViewContent", {
        content_ids: [String(pageData.product.variantId || pageData.product.id)],
        content_type: "product",
        content_name: pageData.product.title,
        content_category: pageData.product.type,
        value: pageData.product.price ? pageData.product.price / 100 : undefined,
        currency: pageData.currency,
      });
    }

    if (pageData.template === "search" && pageData.search && pageData.search.terms) {
      trackMetaEvent("Search", {
        search_string: pageData.search.terms,
        value: pageData.search.resultsCount,
        currency: pageData.currency,
      });
    }

    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event.source !== "product-form" || !event.cartData) return;
        var item = event.cartData;
        trackMetaEvent("AddToCart", {
          content_ids: [String(item.variant_id)],
          content_type: "product",
          content_name: item.product_title,
          value: item.price ? (item.price / 100) * (item.quantity || 1) : undefined,
          currency: pageData.currency,
          num_items: item.quantity,
        });
      });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
